import React from 'react';
import { Result, Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import { ShieldX } from 'lucide-react';
import { useAccountClaims } from '../hooks/useAccountClaims';
import { useAuth } from '../contexts/AuthContext';
import { isPlatformAdmin } from '../rbac/roles';
import type { AccountRole } from '../types/accounts';

interface AccountRoleGuardProps {
  /** Required account role. Currently only `account_owner` is meaningful. */
  role: Exclude<AccountRole, null>;
  /**
   * When true (default), master_admin / super_admin bypass the
   * accountRole check — fixes Wave 3 gap #2 FE half (Janus internal staff
   * was locked out of /rollup by the literal account_owner check).
   */
  bypassPlatformAdmin?: boolean;
  children: React.ReactNode;
}

/**
 * Account-layer counterpart to <RoleGuard>. Where RoleGuard inspects the
 * tenant-level `role` claim, this guard inspects the account-level
 * `accountRole` claim — required for routes like /rollup that span tenants.
 *
 * Master/super admins bypass the check by default so Janus internal staff
 * can reach cross-tenant pages without being assigned account_owner on
 * every tenant (gap #2 FE).
 */
const AccountRoleGuard: React.FC<AccountRoleGuardProps> = ({
  role,
  bypassPlatformAdmin = true,
  children,
}) => {
  const { accountRole } = useAccountClaims();
  const { user } = useAuth();
  const navigate = useNavigate();

  const userRoleStrings: string[] = [
    ...((user as Record<string, unknown> | null)?.roles as string[] | undefined ?? []),
    user?.role,
  ].filter(Boolean) as string[];

  if (bypassPlatformAdmin && isPlatformAdmin(userRoleStrings)) {
    return <>{children}</>;
  }

  if (accountRole !== role) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <Result
          icon={<ShieldX size={64} color="#EF4444" />}
          status="403"
          title="Acesso restrito"
          subTitle="Esta página é exclusiva para proprietários de conta."
          extra={
            <Button type="primary" onClick={() => navigate('/')}>
              Voltar ao Dashboard
            </Button>
          }
        />
      </div>
    );
  }

  return <>{children}</>;
};

export default AccountRoleGuard;
